import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Login from "./pages/Login";
import VerifyOtp from "./pages/VerifyOtp";
import ActiveDevices from "./pages/ActiveDevices";
import UserManagement from "./pages/UserManagement";
import AccessPanel from "./pages/AccessPanel";
import Vendors from "./pages/master/Vendors";
import Customers from "./pages/master/Customers";
import Disciplines from "./pages/master/Disciplines";
import SubDisciplines from "./pages/master/SubDisciplines";
import Products from "./pages/master/Products";
import Services from "./pages/master/Services";
import Certificates from "./pages/master/Certificates";
import Home from "./pages/Home";
import Calendar from "./pages/Calendar";
import Layout from "./Components/Layout";
import { AuthProvider } from "./context/AuthContext";
import { ToastProvider } from "./Components/Toast";
import ProtectedRoute from "./Components/ProtectedRoute";
import CompanyManagement from "./pages/CompanyManagement";
import CompanyForm from "./pages/CompanyForm";
import PastRecords from "./pages/PastRecords";
import Inspectors from "./pages/master/Inspectors";
import Manday from "./pages/Manday";
import Enquiry from "./pages/Enquiry";
import CoordinatorDashboard from "./pages/CoordinatorDashboard";
import DashboardDataView from "./pages/DashboardDataView";
import NotFound from "./pages/NotFound";
import FinanceDashboard from "./pages/finance/FinanceDashboard";
import CustomerInvoice from "./pages/finance/CustomerInvoice";
import InspectionInvoice from "./pages/finance/InspectionInvoice";
import CurrencyRateMaster from "./pages/finance/CurrencyRateMaster";

/**
 * Every route uses pageKey — ProtectedRoute checks canAccessPage(pageKey)
 * which reads the user's UserAccess.pages array set in the Access Panel.
 *
 * No more hardcoded allowedRoles — access is 100% driven by the database.
 */
function App() {
  return (
    <Router>
      <ToastProvider>
      <AuthProvider>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Login />} />
          <Route path="/verify-otp" element={<VerifyOtp />} />

          <Route element={<Layout />}>
            {/* Active Devices — no pageKey, any logged-in user can manage their own sessions */}
            <Route path="/active-devices" element={
              <ProtectedRoute>
                <ActiveDevices />
              </ProtectedRoute>
            } />

            {/* Home — no pageKey, any logged-in user can see it (content is role-shaped inside Home.jsx) */}
            <Route path="/home" element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            } />

            {/* Inspection Calendar — no pageKey, any logged-in user can see it (data is scoped by
                role inside calendarController.js: own enquiries, direct reports', or the whole company) */}
            <Route path="/calendar" element={
              <ProtectedRoute>
                <Calendar />
              </ProtectedRoute>
            } />

            {/* Company Management */}
            <Route path="/company-management" element={
              <ProtectedRoute pageKey="company_management">
                <CompanyManagement />
              </ProtectedRoute>
            } />

            {/* Company add / view / edit — one page, mode picked from the path */}
            {["/company-management/new", "/company-management/:id", "/company-management/:id/edit"].map(path => (
              <Route key={path} path={path} element={
                <ProtectedRoute pageKey="company_management">
                  <CompanyForm />
                </ProtectedRoute>
              } />
            ))}

            {/* Past Records */}
            <Route path="/past-records" element={
              <ProtectedRoute pageKey="past_records">
                <PastRecords />
              </ProtectedRoute>
            } />



            {/* User Management */}
            <Route path="/user-management" element={
              <ProtectedRoute pageKey="user_management">
                <UserManagement />
              </ProtectedRoute>
            } />

            {/* Access Panel */}
            <Route path="/access-panel" element={
              <ProtectedRoute pageKey="access_panel">
                <AccessPanel />
              </ProtectedRoute>
            } />

            {/* Master */}
            <Route path="/master/vendors" element={
              <ProtectedRoute pageKey="master_vendors">
                <Vendors />
              </ProtectedRoute>
            } />

            <Route path="/master/customers" element={
              <ProtectedRoute pageKey="master_customers">
                <Customers />
              </ProtectedRoute>
            } />

            <Route path="/master/discipline" element={
              <ProtectedRoute pageKey="master_discipline">
                <Disciplines />
              </ProtectedRoute>
            } />

            <Route path="/master/subdiscipline" element={
              <ProtectedRoute pageKey="master_subdiscipline">
                <SubDisciplines />
              </ProtectedRoute>
            } />

            <Route path="/master/product" element={
              <ProtectedRoute pageKey="master_product">
                <Products />
              </ProtectedRoute>
            } />

            <Route path="/master/service" element={
              <ProtectedRoute pageKey="master_service">
                <Services />
              </ProtectedRoute>
            } />
            <Route path="/master/certificate" element={
              <ProtectedRoute pageKey="master_certificate">
                <Certificates />
              </ProtectedRoute>
            } />
            <Route path="/master/inspectors" element={
              <ProtectedRoute pageKey="master_inspectors">
                <Inspectors />
              </ProtectedRoute>
            } />

            {/* Enquiry Management */}
            <Route path="/enquiry" element={
              <ProtectedRoute pageKey="enquiry_register">
                <Enquiry />
              </ProtectedRoute>
            } />
            <Route path="/enquiry/manday/new" element={
              <ProtectedRoute pageKey="enquiry_register">
                <Manday />
              </ProtectedRoute>
            } />
            <Route path="/enquiry/manday/:id" element={
              <ProtectedRoute pageKey="enquiry_register">
                <Manday />
              </ProtectedRoute>
            } />

            {/* Finance module */}
            <Route path="/finance/dashboard" element={
              <ProtectedRoute pageKey="finance_dashboard">
                <FinanceDashboard />
              </ProtectedRoute>
            } />
            <Route path="/finance/customer-invoice" element={
              <ProtectedRoute pageKey="finance_customer_invoice">
                <CustomerInvoice />
              </ProtectedRoute>
            } />
            <Route path="/finance/inspection-invoice" element={
              <ProtectedRoute pageKey="finance_inspection_invoice">
                <InspectionInvoice />
              </ProtectedRoute>
            } />
            <Route path="/finance/sales-invoice" element={<Navigate to="/finance/customer-invoice" replace />} />
            <Route path="/finance/currency-rates" element={
              <ProtectedRoute pageKey="finance_currency_rates">
                <CurrencyRateMaster />
              </ProtectedRoute>
            } />

            {/* Dashboard sub-pages */}
            <Route path="/dashboard/coordinator" element={
              <ProtectedRoute pageKey="dashboard_coordinator">
                <CoordinatorDashboard />
              </ProtectedRoute>
            } />
            {/* Drill-down table opened from any dashboard; data is role-scoped by the backend */}
            <Route path="/dashboard/view/:viewKey" element={
              <ProtectedRoute>
                <DashboardDataView />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/customer-vendor" element={
              <ProtectedRoute pageKey="dashboard_customer_vendor">
                <div style={{ padding: "2rem" }}>
                  <h2>Customer Vendor Dashboard</h2>
                </div>
              </ProtectedRoute>
            } />
          </Route>

          {/* 404 — catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
      </ToastProvider>
    </Router>
  );
}

export default App;